import { Button, ButtonProps, SvgIcon } from "@mui/material";
import { useId, useRef, useState } from "react";
import { CoreIconButton, CoreIconButtonProps } from "./Button";
import CoreBadge, { CoreBadgeProps } from "../badge";
import Sliders from "./images/sliders.svg?react";
import Funnel from "./images/funnel.svg?react";
import CoreFilterPopover, {
	CoreFilterCheckboxEntry,
	CoreFilterList,
	CoreFilterPopoverProps,
	CoreFilterSection,
	CoreFilterSectionOption,
} from "../filter";
import i18nPackage from "../../i18n/i18n";
import { useShadowContainer } from "../../editQuiz/shadow-dom-context";

export type CoreFilterButtonProps = (
	| ({ variant: "button" } & Omit<ButtonProps, "variant">)
	| ({ variant?: "icon" } & Omit<
			// Default variant is icon
			CoreIconButtonProps,
			"variant" | "color" | "children" | "onClick" | "aria-haspopup" | "aria-controls" | "tooltip"
	  >)
) & {
	filterCount?: number;
	alternateIcon?: boolean;
	slotProps?: {
		badge?: Omit<CoreBadgeProps, "badgeContent">;
		filterList?: React.ComponentProps<typeof CoreFilterList>;
	};
	filterSections: CoreFilterSectionOption[];
	tooltipText?: string;
	onOpen?: () => void;
} & Omit<CoreFilterPopoverProps, "id" | "anchorEl" | "filterCount" | "children" | "onClose">;

export default function CoreFilterButton({
	variant,
	filterCount,
	alternateIcon,
	filterSections,
	slotProps,
	tooltipText = i18nPackage.t("coreComponents.filter.tooltipText"),
	onApply,
	onClear,
	onOpen,
	...buttonProps
}: CoreFilterButtonProps) {
	const [filterShowing, setFilterShowing] = useState(false);
	const popupId = useId();
	const buttonRef = useRef<HTMLButtonElement>(null);
	const shadowContainer = useShadowContainer(); // Adding focus trap ONLY if in shadow dom

	const handleOpen = () => {
		onOpen?.();
		setFilterShowing(true);
	};

	const commonProps = {
		ref: buttonRef,
		"aria-haspopup": "dialog" as const,
		"aria-controls": filterShowing ? popupId : undefined,
		"aria-label": i18nPackage.t("coreComponents.filter.ariaLabel"),
		onClick: handleOpen,
	};

	const buttonComponent =
		variant === "button" ? (
			<CoreBadge {...slotProps?.badge} badgeContent={filterCount}>
				<Button
					{...buttonProps}
					{...commonProps}
					sx={{
						height: "35px",
						paddingRight: "7px",
						paddingLeft: "7px",
						color: "#333333",
						border: "2px solid #E0E0E0",
						borderRadius: "6px",
						fontSize: "15px",
						fontWeight: "400",
						boxSizing: "border-box",
						textTransform: "none",
						"&:hover": {
							backgroundColor: "#F9F9F9",
						},
						"&:active": {
							backgroundColor: "#F2EFF7",
						},
						"&:focus-visible": {
							outline: "2px solid #0b6f85",
							outlineOffset: "2px",
						},
						"& svg path": {
							fill: "transparent",
							stroke: "#4F4F4F",
						},
					}}
				>
					<div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
						{alternateIcon ? (
							<Funnel width="16" height="16" style={{ marginTop: "2.5px" }} />
						) : (
							<Sliders width="16" height="16" style={{ marginTop: "2.5px" }} />
						)}
						{i18nPackage.t("coreComponents.filter.title")}
					</div>
				</Button>
			</CoreBadge>
		) : (
			<CoreIconButton {...buttonProps} {...commonProps} variant="plain" color="primary" tooltip={tooltipText}>
				<CoreBadge
					{...slotProps?.badge}
					badgeContent={filterCount}
					{...(alternateIcon ? { slotProps: { badge: { style: { scale: "0.75" } } } } : {})}
				>
					{alternateIcon ? (
						<SvgIcon style={{ width: 22, height: 22 }}>
							<Funnel />
						</SvgIcon>
					) : (
						<SvgIcon style={{ width: 32, height: 32 }}>
							<Sliders />
						</SvgIcon>
					)}
				</CoreBadge>
			</CoreIconButton>
		);

	return (
		<>
			{buttonComponent}
			{filterShowing && (
				<CoreFilterPopover
					id={popupId}
					onClose={() => setFilterShowing(false)}
					onApply={onApply}
					onClear={onClear}
					anchorEl={buttonRef.current}
					filterCount={filterCount}
					inShadowDom={!!shadowContainer}
				>
					{filterSections.map((section, i) => {
						const { sectionTitle, alignment } = section;

						return (
							<CoreFilterSection key={`${sectionTitle}-${i}`} alignment={alignment} title={sectionTitle}>
								{"checkboxes" in section ? (
									<CoreFilterList {...slotProps?.filterList}>
										{section.checkboxes.map((option, j) => {
											const { checked, onChange, className, children } = option;
											return (
												<CoreFilterCheckboxEntry key={j} checked={checked} onChange={onChange} className={className}>
													{children}
												</CoreFilterCheckboxEntry>
											);
										})}
									</CoreFilterList>
								) : (
									section.customFilter()
								)}
							</CoreFilterSection>
						);
					})}
				</CoreFilterPopover>
			)}
		</>
	);
}
